import clsx from "clsx";
import { forwardRef, memo, useImperativeHandle, useMemo, useRef } from "react";

const sizeStyles = {
  sm: {
    track: "h-5 w-9",
    thumbSize: "after:h-4 after:w-4",
    thumbOffset: "after:left-0.5",
    thumbTranslate: "peer-checked:after:translate-x-4",
  },
  md: {
    track: "h-6 w-11",
    thumbSize: "after:h-5 after:w-5",
    thumbOffset: "after:left-0.5",
    thumbTranslate: "peer-checked:after:translate-x-5",
  },
  lg: {
    track: "h-7 w-14",
    thumbSize: "after:h-6 after:w-6",
    thumbOffset: "after:left-1",
    thumbTranslate: "peer-checked:after:translate-x-7",
  },
  xl: {
    track: "h-8 w-12",
    thumbSize: "after:h-[26px] after:w-[26px]",
    thumbOffset: "after:left-0.5",
    thumbTranslate: "peer-checked:after:translate-x-4",
  },
};

const ToggleButton = forwardRef(
  (
    {
      className = "",
      trackClassname = "",
      size = "md",
      disabled = false,
      checked,
      defaultChecked,
      ...props
    },
    ref,
  ) => {
    const internalRef = useRef(null);
    useImperativeHandle(ref, () => internalRef.current);

    const resolvedSize = sizeStyles[size] ?? sizeStyles.md;

    const wrapperClassName = useMemo(
      () =>
        clsx(
          "relative inline-flex items-center rounded-full",
          disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
          className,
        ),
      [disabled, className],
    );

    const trackClassName = useMemo(
      () =>
        clsx(
          "relative inline-flex flex-shrink-0 rounded-full border border-transparent transition-colors duration-300 ease-out",
          "after:absolute after:top-1/2 after:translate-x-0 after:-translate-y-1/2 after:rounded-full after:bg-white after:shadow-sm after:transition-transform after:duration-300 after:ease-out after:content-['']",
          "bg-border peer-checked:bg-primary",
          resolvedSize.track,
          resolvedSize.thumbSize,
          resolvedSize.thumbOffset,
          resolvedSize.thumbTranslate,
          disabled &&
            "bg-border/60 peer-checked:bg-primary/50 after:bg-white/80",
          trackClassname,
        ),
      [resolvedSize, disabled],
    );

    return (
      <label className={wrapperClassName}>
        <input
          type="checkbox"
          ref={internalRef}
          className="peer sr-only"
          disabled={disabled}
          checked={checked}
          defaultChecked={defaultChecked}
          {...props}
        />
        <span aria-hidden="true" className={trackClassName} />
      </label>
    );
  },
);

ToggleButton.displayName = "ToggleButton";

const MemoizedToggleButton = memo(ToggleButton);
MemoizedToggleButton.displayName = "ToggleButton";

export default MemoizedToggleButton;
